import axios, { AxiosInstance } from 'axios';
import axiosRetry from 'axios-retry';
import platform from 'platform';
import {
  ApiResult,
  AuthUser,
  AxiosResult,
  Endpoints,
  EndpointsType,
  II18n,
  RequestConfig,
} from '../models';
import { BaseService } from '../services';
import { newGuid } from '../util/Guid';
import { EnvironmentService } from './environment.service';
import { I18nService } from './i18n.service';

const _translations: II18n = {
  es: {
    INVALID: 'No válidos!',
  },
  enUS: {
    INVALID: 'Invalid!',
  },
  ptBR: {
    INVALID: 'Inválido!',
  },
};

class ApiService {
  private axiosInstance: AxiosInstance;

  constructor(
    private endpoint: EndpointsType,
    private translations: II18n = I18nService.emptyDictionary,
  ) {
    let baseURL: string = '';
    switch (this.endpoint) {
      case Endpoints.ACADEMY:
        baseURL = this.baseURLAcademy;
        break;

      case Endpoints.AUTH:
        baseURL = this.baseURLAuth;
        break;

      case Endpoints.CONNECT:
        baseURL = this.baseURLConnect;
        break;
      case Endpoints.CONNECT_REPORT:
        baseURL = this.baseURLConnectReport;
        break;

      case Endpoints.CORE:
        baseURL = this.baseURLCore;
        break;

      case Endpoints.DRIVE:
        baseURL = this.baseURLDrive;
        break;

      case Endpoints.NAVIGATOR:
        baseURL = this.baseURLNavigator;
        break;

      case Endpoints.INTEGRATOR:
        baseURL = this.baseURLIntegrator;
        break;

      case Endpoints.SURVEY:
        baseURL = this.baseURLSurvey;
        break;

      default:
        break;
    }
    this.axiosInstance = axios.create({ baseURL, withCredentials: false });
    axiosRetry(this.axiosInstance, {
      retries: 0,
      retryDelay: (retryCount: number): number => retryCount * 2000,
      retryCondition: (error: any): boolean =>
        error.response == undefined ||
        error.response.status === undefined ||
        error.response.status === 503 ||
        error.response.status === 504,
    });
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error: any) => {
        if (error && error.response) {
          if (error.response.status === 403) {
            window.postMessage({
              apiResult: {
                status403: true,
              },
            });
          }
          if (error.response.status === 401) {
            // window.location.href = '/login';
            if(prompt('Sua sessão expirou ou você tentou acessar um recurso não autorizado! Deseja fazer login novamente?')) {
              window.location.href = '/login';
            }
          }
        }

        return Promise.reject(error);
      },
    );

    this.axiosInstance.interceptors.request.use(
      (config) => {
        const gintercept: any = (window as any)['xintercept'];
        if(gintercept) {
          return gintercept(config);  
        }
        if (process.env.NODE_ENV === 'development') {
          // config.baseURL = 'http://localhost:3000';
        }
    
        return config;
      },
      (error) => {
        // Lida com erros antes de enviar a requisição
        return Promise.reject(error);
      }
    );

    this.translations = {
      es: {
        ...I18nService.emptyDictionary.es,
        ...translations.es,
        ..._translations.es,
      },
      enUS: {
        ...I18nService.emptyDictionary.enUS,
        ...translations.enUS,
        ..._translations.es,
      },
      ptBR: {
        ...I18nService.emptyDictionary.ptBR,
        ...translations.ptBR,
        ..._translations.es,
      },
    };
  }

  private get baseURL(): string {
    var altapi = sessionStorage.getItem('alternative-api');
    if (altapi != null) {
      return '' + sessionStorage.getItem('alternative-api');
    }

    // Runtime config (config.js carregado no index.html)
    const config = (window as any).__APP_CONFIG__;
    if (config?.API_URL) return config.API_URL;

    // Fallback hardcoded (produção atual)
    return 'https://api.bdbsystem.com.br';
  }

  public static getMicrofrontURL(route?: string): string {
    if (EnvironmentService.isProd) {
      return 'https://microfront-connect.bdbsystem.com.br/' + route;
    } else if (EnvironmentService.isDev) {
      return 'https://microfront-connect-dev.bdbsystem.com.br/' + route;
    } else {
      return 'http://localhost:4200/' + route;
    }
  }

  private get baseURLAcademy(): string {
    return `${this.baseURL}/academy`;
  }

  private get baseURLAuth(): string {
    return `${this.baseURL}/auth`;
  }

  private get baseURLConnect(): string {
    var altapi = localStorage.getItem('alternative-api');
    if (altapi != null) {
      return '' + localStorage.getItem('alternative-api');
    }

    if(window.location.href.includes('localhost')) {
      return `${this.baseURL}`;
    }


    return `${this.baseURL}/connect`;
    // return `https://4417-179-189-43-118.sa.ngrok.io`;
  }

  private get baseURLConnectReport(): string {
    if(window.location.href.includes('localhost')) {
      return 'http://192.168.15.100:5000';
    }

    const config = (window as any).__APP_CONFIG__;
    if (config?.REPORT_URL) return config.REPORT_URL;

    return 'https://reportserver.bdbsystem.com.br/connect';
  }

  private get baseURLCore(): string {
    return `${this.baseURL}/core`;
  }

  private get baseURLDrive(): string {
    return `${this.baseURL}/drive`;
  }

  private get baseURLNavigator(): string {
    return `${this.baseURL}/navigator`;
  }

  private get baseURLIntegrator(): string {
    return `${this.baseURL}/navigator-integrator`;
  }

  private get baseURLSurvey(): string {
    return `${this.baseURL}/navigator-survey`;
  }

  private addHeaderAuth = (config: RequestConfig): RequestConfig => {
    if (!config.headers) {
      config.headers = {};
    }

    config.headers['x-platform'] = platform;
    config.headers['x-tracker'] = newGuid();

    // @ts-ignore
    config.headers['x-enviroment'] = window.appEnviroment;

    const currentUser = AuthUser.currentUser;
    if (currentUser && currentUser.id) {
      config.headers['x-account'] = currentUser.accountId;
      config.headers['x-language'] = BaseService.userLanguage;
      config.headers['Authorization'] = `Bearer ${currentUser.token}`;
    }

    return config;
  };

  private getMessageError(apiResult: ApiResult): string | undefined {
    let message: string | undefined;
    if (!apiResult || !apiResult.success) {
      message = '';

      if (apiResult.message && apiResult.message.length) {
        let join = '';
        apiResult.message.forEach((msg) => {
          message += `${join}${I18nService.translate(this.translations, msg)}`;
          join = ', ';
        });
      }
    }

    return message;
  }

  public checkTokenValidity = (apiResult: ApiResult): void => {
    // NOTIFICA o usuário sobre o tempo restante da vida útil do token de autenticação
    if (apiResult && apiResult.tokenValidTotalMinutesTo) {
      if (apiResult.tokenValidTotalMinutesTo <= 0) {
        let redirect = '';
        if (EnvironmentService.isDev) {
          redirect = `?redirect=${window.location.origin}`;
        }

        const config = (window as any).__APP_CONFIG__;
        const authUrl = config?.AUTH_URL || 'https://auth.bdbsystem.com.br';
        window.location.href = `${authUrl}/${redirect}`;
      } else {
        window.postMessage({
          apiResult: {
            tokenValidTotalMinutesTo: apiResult.tokenValidTotalMinutesTo,
          },
        });
      }
    }
  };

  public async get<Result = any>(
    url: string,
    config: RequestConfig = {},
  ): Promise<ApiResult<Result>> {
    const axiosResult: AxiosResult<Result> = await this.axiosInstance.get(
      url,
      this.addHeaderAuth(config),
    );
    const apiResult: ApiResult<Result> = axiosResult.data;
    apiResult.errorMessage = this.getMessageError(apiResult);

    this.checkTokenValidity(apiResult);

    return apiResult;
  }

  public async post<Result = any>(
    url: string,
    data: any,
    config: RequestConfig = {},
  ): Promise<ApiResult<Result>> {
    const axiosResult: AxiosResult<Result> = await this.axiosInstance.post(
      url,
      data,
      this.addHeaderAuth(config),
    );
    const apiResult: ApiResult<Result> = axiosResult.data;
    apiResult.errorMessage = this.getMessageError(apiResult);

    this.checkTokenValidity(apiResult);

    return apiResult;
  }


  public async postOnServer<Result = any>(
    url: string,
    data: any,
    config: RequestConfig = {},
  ): Promise<ApiResult<Result>> {
    const axiosResult: AxiosResult<Result> = await this.axiosInstance.post(
      url,
      data,
      this.addHeaderAuth(config),
    );
    const apiResult: ApiResult<Result> = axiosResult.data;
    apiResult.errorMessage = this.getMessageError(apiResult);

    this.checkTokenValidity(apiResult);

    return apiResult;
  }

  public async put<Result = any>(
    url: string,
    data: any,
    config: RequestConfig = {},
  ): Promise<ApiResult<Result>> {
    const axiosResult: AxiosResult<Result> = await this.axiosInstance.put(
      url,
      data,
      this.addHeaderAuth(config),
    );
    const apiResult: ApiResult<Result> = axiosResult.data;
    apiResult.errorMessage = this.getMessageError(apiResult);

    this.checkTokenValidity(apiResult);

    return apiResult;
  }

  public async delete<Result = any>(
    url: string,
    config: RequestConfig = {},
  ): Promise<ApiResult<Result>> {
    const axiosResult: AxiosResult<Result> = await this.axiosInstance.delete(
      url,
      this.addHeaderAuth(config),
    );
    const apiResult: ApiResult<Result> = axiosResult.data;
    apiResult.errorMessage = this.getMessageError(apiResult);

    this.checkTokenValidity(apiResult);

    return apiResult;
  }

  /* Não é mais necessário utilzar está técnica
     RLG: 16-10-2021
  public async healthCheck(): Promise<void> {
    await this.axiosInstance.get('/health-check');
  }
  */
}

export { ApiService };

